/**
 * Common UI action icon constants
 * Centralized for consistency across the application
 */
export const intrusionIcons = {
  // Custom-composed identifiers map to bespoke intrusion icons from the design reference.
  INTRUSION_BURGLARY: 'mask',
  INTRUSION_ALARM_24HR_SUPERVISORY: 'timer',
  INTRUSION_FIRE: 'fire',
  INTRUSION_FIRE_SUPERVISORY: 'sensor-fire',
  INTRUSION_CO: 'smog',
  INTRUSION_GAS: 'cloud-exclamation',
  INTRUSION_HIGH_TEMPERATURE: 'temperature-three-quarters',
  INTRUSION_LOW_TEMPERATURE: 'temperature-quarter',
  INTRUSION_MEDICAL: 'kit-medical',
  INTRUSION_PANIC: 'alarm-exclamation',
  INTRUSION_WATERFLOW: 'faucet',
  INTRUSION_WATER_LEAKAGE: 'water',
  INTRUSION_PENDANT: 'circle-dot',
  INTRUSION_TAMPER: 'hammer',
  INTRUSION_RF_JAM: 'wifi-slash',
  INTRUSION_FAULT: 'circle-exclamation',
  INTRUSION_DURESS: 'shield-keyhole',
  INTRUSION_PERSONAL_EMERGENCY: 'heart-circle-exclamation',
  INTRUSION_HOLDUP: 'hand',
  INTRUSION_SPRINKLER: 'shower',
  INTRUSION_QUICK_BYPASS: 'shield-slash',
  INTRUSION_BURGLARY_UNVERIFIED: 'mask',
  INTRUSION_HOLDUP_UNVERIFIED: 'circle-question',
  INTRUSION_HIGH_TEMP_WARNING: 'temperature-three-quarters',
  INTRUSION_LOW_TEMP_WARNING: 'temperature-quarter',
  INTRUSION_LOW_BATTERY: 'battery-quarter',
  INTRUSION_LOW_SENSITIVITY: 'eye-low-vision',
  INTRUSION_TROUBLE: 'triangle-exclamation',
  INTRUSION_GENERAL: 'circle-info',
  INTRUSION_UNKNOWN: 'circle-question',
} as const;

export type IntrusionIconName = typeof intrusionIcons[keyof typeof intrusionIcons];
export const INTRUSION_ICON_NAMES = new Set<IntrusionIconName>(Object.values(intrusionIcons));
export const isIntrusionIconName = (value: unknown): value is IntrusionIconName =>
  typeof value === 'string' && INTRUSION_ICON_NAMES.has(value as IntrusionIconName);

export const ICON_COMPONENT = {
  BODYCAM: 'BodycamIcon',
} as const;

export const ICON = {
  ADD: 'add',
  ACTIONS: 'screwdriver-wrench',
  ARROW_UP: 'arrow-up',
  BAN: 'ban',
  BATTERY: 'battery-empty',
  BELL: 'bell',
  BOLT: 'bolt',
  BOX_ARCHIVE: 'box-archive',
  BRIEFCASE: 'briefcase',
  BUILDING: 'building',
  CAMERA: 'video',
  CARET_DOWN: 'caret-down',
  CARET_LEFT: 'caret-left',
  CARET_RIGHT: 'caret-right',
  CARET_UP: 'caret-up',
  CHART_LINE: 'chart-line',
  CHECK: 'check',
  CHEVRON_DOWN: 'chevron-down',
  CHEVRON_LEFT: 'chevron-left',
  CHEVRON_RIGHT: 'chevron-right',
  CIRCLE: 'circle',
  CIRCLE_CHECK: 'circle-check',
  CIRCLE_EXCLAMATION: 'circle-exclamation',
  CIRCLE_INFO: 'circle-info',
  CIRCLE_PLAY: 'circle-play',
  CIRCLE_QUESTION: 'circle-question',
  CLIPBOARD_LIST: 'clipboard',
  CLOCK: 'clock',
  CLOSE: 'xmark',
  COMMENTS: 'comments',
  COPY: 'copy',
  DELETE: 'trash',
  DOOR: 'door-open',
  DOOR_CLOSED: 'door-closed',
  DOWN_FROM_LINE: 'down-from-line',
  EARTH_AMERICAS: 'earth-americas',
  EDIT: 'pen-to-square',
  ELLIPSIS_VERTICAL: 'ellipsis-vertical',
  ERROR: 'triangle-exclamation',
  EXCLAMATION: 'exclamation',
  EXPAND: 'maximize',
  FILE_CSV: 'file-csv',
  FILE_LINES: 'file-lines',
  FILTER: 'filter',
  GEAR: 'gear',
  HARD_DRIVE: 'hard-drive',
  HISTORY: 'clock-rotate-left',
  ID_CARD_CLIP: 'id-card-clip',
  INTRUSION_PANEL: 'house-lock',
  KEY: 'key',
  KEY_SKELETON: 'key-skeleton',
  LAYER_GROUP: 'layer-group',
  LINK: 'link',
  LOCATION: 'location-dot',
  LOCATION_CROSSHAIRS: 'location-crosshairs',
  LOCK: 'lock-keyhole',
  MAGNIFYING_GLASS: 'magnifying-glass',
  MICROCHIP: 'microchip',
  MICROPHONE: 'microphone',
  MINUS: 'minus',
  MOON: 'moon',
  NETWORK: 'network-wired',
  OBJECT_DETECTED: 'user',
  PALETTE: 'palette',
  PAUSE: 'pause',
  PEN: 'pen',
  PERSON: 'user',
  PERSON_WALKING: 'person-walking',
  PLAY: 'play',
  PLUG_ERROR: 'plug-circle-exclamation',
  PLUS: 'plus',
  ROUTE: 'route',
  SAVE: 'save',
  SCHEDULE_HOLIDAY: 'island-tropical',
  SENSOR: 'sensor-on',
  SHIELD: 'shield',
  SHIELD_ALERT: 'shield-exclamation',
  SHIELD_CHECK: 'shield-check',
  SORT: 'sort',
  SORT_DOWN: 'sort-down',
  SORT_UP: 'sort-up',
  SPARKLES: 'sparkles',
  SPINNER: 'spinner',
  SQUARE: 'square',
  SQUARE_CHECK: 'square-check',
  SQUARE_PLUS: 'square-plus',
  SWITCH: 'toggle-on',
  TABLE: 'table',
  THUMBS_DOWN: 'thumbs-down',
  THUMBS_UP: 'thumbs-up',
  UNDO: 'arrow-rotate-left',
  UNKNOWN: 'question',
  UNLOCK: 'lock-keyhole-open',
  UPGRADE: 'arrow-up-from-arc',
  UP_RIGHT_FROM_SQUARE: 'up-right-from-square',
  UP_TO_LINE: 'up-to-line',
  USER: 'user',
  USERS: 'users',
  VIDEO_SLASH: 'video-slash',
  VIEW: 'eye',
  VIEW_SLASH: 'eye-slash',
  WIFI: 'wifi',
  ...intrusionIcons,
} as const;
